import _ from 'lodash';
import store from '@/store';
import { getOnlinePlanName, getPlansMap, sortByPlanList } from '@/common/product';
import { isEmptyVal } from '@/utils';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { currentPstDate, dateTime, dateTimeFormatWithClientZone, getNextPstCycleDate } from '@/utils/dateformat';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import { translation } from '@/plugins/i18n';
import { transferMoney } from '@/utils/money';
import { logger } from '@/utils/log';
import { getPlanTypeName } from '@/common/service/parse/parse-product-name';
import {
  computedCallingPlans,
  computedPbxBasePlans,
  computedPbxNonBasePlans,
  isZoomPhone,
  PBX_SUB_TYPES,
} from '@/common/service/computers/plans/pbx';
import {
  getPlanNamesWithBillingCycle,
  isFromCancelCampaign,
  thresholdCouponUtil,
} from '@/views/config/coupon/helper/utils';
import {
  cancellationCouponSource,
  couponFetchStatus,
  couponSourceTypeMap,
} from '@/views/config/coupon/helper/constant';
import { plansof } from '@/utils/chain';

const isOneTimePlan = (plan = {}) => {
  return plan?.isPPA || plan?.isOneMonth || plan.billing_cycle === BILLING_CYCLE.PAY_PER_ATTENDEE || plan.billing_cycle === BILLING_CYCLE.ONE_TIME || plan.isOneTime;
}

const isFreeTrial = (plan = {}) => {
  return plan?.isFreeTrial;
}

const templateTypeMap = {
  promoEdit: flowTypes.edit,
  promoAddAddon: flowTypes.add2cart,
  promoCancel: cancellationCouponSource,
};

export const getBillingCycleViewForTnc = (billingCycle = '') => {
  switch (billingCycle) {
    case BILLING_CYCLE.MONTHLY:
    case BILLING_CYCLE.PAY_AS_YOU_GO:
    case BILLING_CYCLE.PAY_PER_ATTENDEE:
      return translation('common.month');
    case BILLING_CYCLE.ANNUAL:
      return translation('common.year');
  }
};

export const getBillingCycleForTncEditFlow = (billingCycle = '') => {
  switch (billingCycle) {
    case BILLING_CYCLE.ANNUAL:
      return translation("billing.common.billing_cycle.yearly");
    case BILLING_CYCLE.MONTHLY:
    default:
      return translation("billing.common.billing_cycle.monthly");
  }
};

/**
 * merge multiCouponPlanList and boughtPlans
 * @param multiCouponPlanList
 * @param boughtPlansMap
 * @return {*}
 */
export const generateMultiCouponPlanList = (multiCouponPlanList = [], boughtPlansMap = {}) => {
  return _.chain(multiCouponPlanList)
    .map((p) => {
      const { discountedTotalAmount = 0, totalDiscountAmount = 0, couponExpireDate, ...rest } = p || {};
      const plan = (_.get(boughtPlansMap, [p?.planCode]) ?? _.get(boughtPlansMap, [p?.priceMappingPlanCode])) || {};
      const totalAmount = discountedTotalAmount + totalDiscountAmount;
      const nextAmount = isEmptyVal(plan) ? undefined : (totalAmount / (plan.cartInfo?.selected_number ?? 1));

      return {
        ...rest,
        ...plan,
        nextAmount,
        totalAmount, // total amount
        discountedTotalAmount, // total amount after discount
        totalDiscountAmount, // total discount amount
        couponExpireDate: dateTimeFormatWithClientZone(couponExpireDate),
      };
    })
    .filter((p) => !isOneTimePlan(p) && !isFreeTrial(p))
    .value();
};

export const parseMultiCouponPlanList = ({
   multiCouponPlanList = [],
   boughtPlansMap = {},
}) => {
  const { type: flowType, editConfig = {} } = pageConfig || {};
  const oldPlanCode = editConfig.oldPlanCode;

  return _.filter(multiCouponPlanList, (p) => {
    const curPlanCode = p?.planCode;
    const priceMappingPlanCode = p?.priceMappingPlanCode;
    const plan = _.get(boughtPlansMap, [curPlanCode]) ?? _.get(boughtPlansMap, [priceMappingPlanCode]);
    const planExist = isEmptyVal(plan) === false;

    if (flowType === flowTypes.edit) {
      // need exclude increase(mapping planCode)/origin std price(planCode) plan code
      return planExist && !_.includes([curPlanCode, priceMappingPlanCode], oldPlanCode);
    }

    // For add zoom phone case, need filter have purchased pbx plans, due to the shopping cart already have pbx selected plans
    if (flowType === flowTypes.add2cart
      && isZoomPhone(editConfig?.planType)
      && isZoomPhone(plan?.type)) {
      return false;
    }

    return planExist;
  });
};

export const getMatchDiscountTemplateList = (tncMessage = '') => {
  const discountTemplateRegex = /(#IF\{has_discount_products\})([\w\W]+?)(#ENDIF\{has_discount_products\})/gi;
  const nonDiscountTemplateRegex = /(#IF\{has_non_discount_products\})([\w\W]+?)(#ENDIF\{has_non_discount_products\})/gi;

  return {
    matchDiscountTemplateList: discountTemplateRegex.exec(tncMessage),
    matchNonDiscountTemplateList: nonDiscountTemplateRegex.exec(tncMessage),
  };
};

/**
 *
 * @param selectedPlans {PlanItemBean}
 * @param discountPlans {DiscountMapBean}
 * @param amountPlansMap {SingleAmountPlansMapBean}
 * @return {SplitReplaceSubscriptionPlanMapBean}
 */
export const parsePurchasingPlans = ({
   selectedPlans = [],
   discountPlans = {},
   amountPlansMap = {},
   multiCouponPlanList = [],
}) => {
  const multiplePlanMap = _.reduce(multiCouponPlanList, (pre, p) => {
    const curPlanCode = p?.planCode;
    const priceMappingPlanCode = p?.priceMappingPlanCode;

    return {
      ...pre,
      ...(curPlanCode ? {[curPlanCode]: p} : {}),
      ...(priceMappingPlanCode ? {[priceMappingPlanCode]: p} : {})
    }
  }, {});

  /**@type {ReplaceSubscriptionPlanBean[]} */
  const planList = _.chain(selectedPlans)
    .filter((plan) => !isOneTimePlan(plan) && !isFreeTrial(plan)) // filter one month plan
    .map((plan) => {
      const planCode = plan?.plan_code;
      const { discountAmount: totalDiscountAmount = 0, expireDate = '' } = discountPlans[planCode] || {};
      let totalAmount = _.get(amountPlansMap, planCode);
      const originBoughtPlan = _.get(multiplePlanMap, planCode);
      const discountedTotalAmount = totalAmount - totalDiscountAmount;

      if (plan && originBoughtPlan) {
        const quantity = plan.cartInfo?.selected_number;
        totalAmount = originBoughtPlan.nextAmount * quantity;
      }

      return {
        ...plan,
        planCode,
        totalAmount, // before discount total amount
        couponExpireDate: dateTimeFormatWithClientZone(expireDate), // coupon expire date
        totalDiscountAmount, // total discount amount
        discountedTotalAmount, // after discount total amount
      };
    })
    .value();

  return splitSelectedPlansByDiscount(planList);
};

/**
 *
 * @param planList {ReplaceSubscriptionPlanBean[]}
 * @return {SplitReplaceSubscriptionPlanMapBean}
 */
export const splitSelectedPlansByDiscount = (planList = []) => {
  return _.chain(planList)
    .map((plan) => {
      let nextInvoiceDate = '';

      const billingCycle = plan?.billing_cycle;
      const billingCycleI18n = getBillingCycleViewForTnc(billingCycle);
      const nextInvoicePstDateMap = getNextPstCycleDate({ formatDateTime: dateTime });

      if ([BILLING_CYCLE.MONTHLY,
        BILLING_CYCLE.PAY_AS_YOU_GO,
        BILLING_CYCLE.PAY_PER_ATTENDEE
      ].includes(billingCycle)) {
        nextInvoiceDate = nextInvoicePstDateMap.nextMonthly;
      } else if (billingCycle === BILLING_CYCLE.ANNUAL) {
        nextInvoiceDate = nextInvoicePstDateMap.nextAnnual;
      }

      return {
        ...plan,
        nextInvoiceDate,
        billingCycle: billingCycleI18n,
        productName: getOnlinePlanName(plan),
      };
    })
    .reduce((pre, plan) => {
      if (plan.totalDiscountAmount > 0) {
        pre.discountPlans.push(plan);
      } else {
        pre.nonDiscountPlans.push(plan);
      }

      return pre;
    }, {
      discountPlans: [],
      nonDiscountPlans: [],
    })
    .value();
};

export const getPlanListViewTemplate = ({
                                          planListMap = {},
                                          discountTemplate = '',
                                          nonDiscountTemplate = ''
                                        }) => {
  const { discountPlans = [], nonDiscountPlans = [] } = planListMap;

  const discountViewTemplate = getTncMessageWithTemplate(discountPlans, discountTemplate);
  const nonDiscountViewTemplate = getTncMessageWithTemplate(nonDiscountPlans, nonDiscountTemplate);

  return _.chain([discountViewTemplate, nonDiscountViewTemplate])
    .filter(Boolean)
    .join('')
    .value();
};

const getTncMessageWithTemplate = (plans = [], sourceTemplate = '') => {
  return _.chain(plans)
    .reduce((pre, plan) => {
      let template = sourceTemplate;

      template = template.replace(/\${with_discount_product_name}/g, plan.productName);
      template = template.replace(/\${without_discount_product_name}/g, plan.productName);
      template = template.replace(/\${product_next_invoice_date}/g, plan.nextInvoiceDate);
      template = template.replace(/\${product_amount_with_discount}/g, transferMoney(plan.discountedTotalAmount));
      template = template.replace(/\${product_billing_cycle}/g, plan.billingCycle);
      template = template.replace(/\${product_coupon_end_date}/g, plan.couponExpireDate);
      template = template.replace(/\${product_amount_without_discount}/g, transferMoney(plan.totalAmount));

      return [...pre, template];
    }, [])
    .join('')
    .value();
};

const getMatchTemplateRegexList = (tncMessage) => {
  // for cancellation coupon cases
  const cancelTemplateRegex = /(#IF\{cancel_with_promotion_plans_title\})([\w\W]+?)(#ENDIF\{cancel_with_promotion_plans_title\})/gi;
  // for M2M, M2A, A2A, A2M cases
  const promoEditTemplateRegex = /(#IF\{promotion_edit_plans_title\})([\w\W]+?)(#ENDIF\{promotion_edit_plans_title\})/gi;
  // for promotion add addon cases
  const promoAddonTemplateRegex = /(#IF\{promotion_add_on_plans_title\})([\w\W]+?)(#ENDIF\{promotion_add_on_plans_title\})/gi;

  const matchCancelTemplateRegexList = cancelTemplateRegex.exec(tncMessage);
  const matchPromoEditTemplateRegexList = promoEditTemplateRegex.exec(tncMessage);
  const matchPromoAddonTemplateRegexList = promoAddonTemplateRegex.exec(tncMessage);

  return [
    { type: templateTypeMap.promoCancel, list: matchCancelTemplateRegexList },
    { type: templateTypeMap.promoEdit, list: matchPromoEditTemplateRegexList },
    { type: templateTypeMap.promoAddAddon, list: matchPromoAddonTemplateRegexList },
  ];
};

const getReplaceTemplateWithFilterField = (
  tncMessage = '',
  matchTemplateRegexList = [],
  needRenderTemplate = false,
) => {
  const currentTemplate = needRenderTemplate ? _.get(matchTemplateRegexList, [2], '') : '';
  return _.replace(tncMessage, _.get(matchTemplateRegexList, [0]), currentTemplate);
};

const replaceTncMessageByPromoType = (tncMessage = "") => {
  const flowType = pageConfig?.type;

  const templateType = isFromCancelCampaign() ?
    templateTypeMap.promoCancel : (isUpgradeFlow()
      ? templateTypeMap.promoEdit : flowType);

  const templateMapList = getMatchTemplateRegexList(tncMessage);

  _.forEach(templateMapList, (item) => {
    const needRender = templateType === item.type;
    tncMessage = getReplaceTemplateWithFilterField(tncMessage, item.list, needRender);
  });

  return tncMessage;
};

const parseForPlanType = {
  [planTypes.ZOOM_PHONE]: (boughtPlansMap = {}, purchasingPlanList = []) => {
    // existing
    const boughtCallingPlans = computedCallingPlans(_.values(boughtPlansMap));
    // under purchasing
    const purchasingCallingPlans = computedCallingPlans(purchasingPlanList);

    return {
      existingCycleKey: getBillingCycleForTncEditFlow(boughtCallingPlans?.[0]?.billing_cycle),
      purchasingCycleKey: getBillingCycleForTncEditFlow(purchasingCallingPlans?.[0]?.billing_cycle),
    };
  },
  common: (boughtPlansMap = {}, purchasingPlanList = [], oldPlanCode) => {
    return {
      existingCycleKey: getBillingCycleForTncEditFlow(boughtPlansMap?.[oldPlanCode]?.billing_cycle),
      purchasingCycleKey: getBillingCycleForTncEditFlow(purchasingPlanList?.[0]?.billing_cycle),
    }
  },
};

const parseTncMessageForEditFlow = (
  tncMessage = '',
  boughtPlansMap = {},
  purchasingPlanList = [],
) => {
  const { oldPlanCode, planType } = pageConfig?.editConfig || {};
  const handler = parseForPlanType[planType] ?? parseForPlanType.common;
  const { existingCycleKey, purchasingCycleKey } = handler?.apply(null, [boughtPlansMap, purchasingPlanList, oldPlanCode]);

  const newPurchasingProductName = getOnlinePlanName(_.get(purchasingPlanList, "0"));
  const existingProductName = getOnlinePlanName(_.get(boughtPlansMap, oldPlanCode));

  tncMessage = tncMessage?.replace(/\${new_discount_product_name_list}/g, newPurchasingProductName);
  tncMessage = tncMessage?.replace(/\${existing_product_name_list}/g, existingProductName);
  tncMessage = tncMessage.replace(/\${new_discount_product_billing_cycle}/g, translation(purchasingCycleKey));
  tncMessage = tncMessage.replace(/\${existing_product_billing_cycle}/g, translation(existingCycleKey));

  return tncMessage;
};

const parseTncMessageForUpgradeFlow = (
  tncMessage = '',
  boughtPlans = [],
  boughtPlansMap = {},
  purchasingPlanList = [],
) => {
  const { sourcePlanType, planType } = pageConfig?.editConfig || {};
  const existingPlan = plansof(boughtPlans).type(sourcePlanType).get(0) ?? {};
  const oldPlanCode = existingPlan?.plan_code;

  const { existingCycleKey, purchasingCycleKey } = parseForPlanType.common(boughtPlansMap, purchasingPlanList, oldPlanCode);
  const newPlan = plansof(purchasingPlanList).type(planType).get(0);

  tncMessage = tncMessage?.replace(/\${new_discount_product_name_list}/g, getOnlinePlanName(newPlan));
  tncMessage = tncMessage?.replace(/\${existing_product_name_list}/g, getOnlinePlanName(existingPlan));
  tncMessage = tncMessage.replace(/\${new_discount_product_billing_cycle}/g, translation(purchasingCycleKey));
  tncMessage = tncMessage.replace(/\${existing_product_billing_cycle}/g, translation(existingCycleKey));

  return tncMessage;
}

function isUpgradeFlow() {
  return [flowTypes.pro2prop, flowTypes.pro2bundle, flowTypes.biz2bundle, flowTypes.pro2biz, flowTypes.prop2bundle].includes(pageConfig?.type);
}

function isEditFlow() {
  return pageConfig?.type === flowTypes.edit;
}

const filterPurchasingPlansByBoughtPlans = (selectedPlans = [], boughtPlans = [], discountPlanMap = {}) => {
  if (flowTypes.edit === pageConfig?.type) {
    const planType = pageConfig.editConfig?.planType;

    if ([planTypes.ZOOM_PHONE, planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS].includes(planType)) {
      const hasBoughtBasePlan = computedPbxBasePlans(boughtPlans)?.length > 0;
      const boughtPbxPlans = computedPbxNonBasePlans(boughtPlans);

      return _.filter(selectedPlans, (p) => {
        // is has bought zoom phone base plan
        const needFilterPbxBasePlan = isZoomPhone(p.type) && hasBoughtBasePlan && p.sub_type === PBX_SUB_TYPES.BASE;

        // zoom phone plan and not got discount and have purchased
        const needFilterBoughtPbxPlan = isZoomPhone(p.type)
          && !_.has(discountPlanMap, p.plan_code)
          && _.some(boughtPbxPlans, (x) => p.plan_code === x.plan_code);

        return !(needFilterPbxBasePlan || needFilterBoughtPbxPlan);
      });
    }
  }

  return selectedPlans;
}

// for new terms & condition for add addon/edit/cancellation flow
const parseTncCommonMessage = (tncMessage = '') => {
  const getters = store.getters;

  const planType = pageConfig.editConfig?.planType;
  const boughtPlansMap = getPlansMap(getters.boughtPlans);
  const { plans: purchasingAmountPlansMap } = getters.getSelectedPlansAmount;
  const { discountPlans: purchasingDiscountPlansMap } = getters.discountWithAmountMap;

  const multiCouponPlanList = generateMultiCouponPlanList(getters.multiCouponPlanList, boughtPlansMap);
  const purchasingPlanList = filterPurchasingPlansByBoughtPlans(getters.getSelectedPlans, getters.boughtPlans, purchasingDiscountPlansMap);
  const existingPlanList = parseMultiCouponPlanList({ multiCouponPlanList, boughtPlansMap });

  const { matchDiscountTemplateList, matchNonDiscountTemplateList } = getMatchDiscountTemplateList(tncMessage);
  const discountTemplate = _.get(matchDiscountTemplateList, [2], '').trim();
  const nonDiscountTemplate = _.get(matchNonDiscountTemplateList, [2], '').trim();

  const purchasingPlanListMap = parsePurchasingPlans({
    selectedPlans: purchasingPlanList,
    discountPlans: purchasingDiscountPlansMap,
    amountPlansMap: purchasingAmountPlansMap,
    multiCouponPlanList,
  });

  const existingPlanListMap = splitSelectedPlansByDiscount(existingPlanList);

  logger.info('purchasingPlanListMap: ', purchasingPlanListMap);
  logger.info('existingPlanListMap:', existingPlanListMap);

  // be purchasing plan list template (being purchase flow with marketing coupon)
  const purchasingPlanViewTemplate = getPlanListViewTemplate({
    planListMap: purchasingPlanListMap,
    discountTemplate,
    nonDiscountTemplate,
  });

  // existing plan list template (for multi coupon case)
  const existingPlanViewTemplate = getPlanListViewTemplate({
    planListMap: existingPlanListMap,
    discountTemplate,
    nonDiscountTemplate,
  });

  tncMessage = _.replace(tncMessage, _.get(matchDiscountTemplateList, [0]), purchasingPlanViewTemplate);
  tncMessage = _.replace(tncMessage, _.get(matchNonDiscountTemplateList, [0]), existingPlanViewTemplate);
  // replace part tncMessage with different case (add addon/edit/cancellation coupon)
  tncMessage = replaceTncMessageByPromoType(tncMessage);

  // just for upgrade flow
  if (isUpgradeFlow()) {
    tncMessage = parseTncMessageForUpgradeFlow(tncMessage, getters.boughtPlans, boughtPlansMap, purchasingPlanList);
  } else if (isEditFlow()) {
    tncMessage = parseTncMessageForEditFlow(tncMessage, boughtPlansMap, purchasingPlanList);
  } else {
    const productNameI18n = getPlanTypeName(planType) || _.get(purchasingPlanList, '0.typeNameKey');
    tncMessage = tncMessage?.replace(/\${new_discount_product_name_list}/g, translation(productNameI18n));
    tncMessage = tncMessage?.replace(/\${existing_product_name_list}/g, translation(productNameI18n));
  }

  return tncMessage;
};

// for threshold coupon terms and condition
const parseThresholdTnc = () => {
  const getters = store.getters;
  let { tncMessage = '' } = getters.couponState;
  const { nextMonthly: monthlyNextChargeDate, nextAnnual: annualNextChargeDate } = getNextPstCycleDate({ formatDateTime: dateTime });

  if (!tncMessage) return '';

  const { annualAmount, monthlyAmount } = getters.getSelectedPlansAmount;
  const { monthlyDiscount, annualDiscount, thresholdResult } = getters.discountWithAmountMap;
  const thresholdDiscountPlans = thresholdResult.thresholdDiscountPlans || {};

  const monthlyRegex = /(#IF\{has_threshold_monthly_products\})([\w\W]+?)(#ENDIF\{has_threshold_monthly_products\})/gi;
  const annualRegex = /(#IF\{has_threshold_annual_products\})([\w\W]+?)(#ENDIF\{has_threshold_annual_products\})/gi;

  const matchMonthlyList = monthlyRegex.exec(tncMessage);
  const matchAnnualList = annualRegex.exec(tncMessage);

  const monthlyTnc = _.get(matchMonthlyList, [2], '').trim();
  const annualTnc = _.get(matchAnnualList, [2], '').trim();

  const { monthly: monthlyNameList, annual: annualNameList } = getPlanNamesWithBillingCycle(thresholdDiscountPlans);

  const setBillingCycleRow = (content = '') => `<div class="threshold-coupon-cycle-row">${content}</div>`;

  const monthReplace = thresholdCouponUtil.replaceTncInfo(monthlyTnc, {
    productNameList: monthlyNameList.join(''),
    amountWithoutDiscount: transferMoney(monthlyAmount, {}),
    nextInvoiceDate: monthlyNextChargeDate,
    totalDiscount: monthlyDiscount,
  });

  tncMessage = tncMessage?.replace(_.get(matchMonthlyList, [0]), setBillingCycleRow(monthReplace));

  const annualReplace = thresholdCouponUtil.replaceTncInfo(annualTnc, {
    productNameList: annualNameList.join(''),
    amountWithoutDiscount: transferMoney(annualAmount, {}),
    nextInvoiceDate: annualNextChargeDate,
    totalDiscount: annualDiscount,
  });

  tncMessage = tncMessage?.replace(_.get(matchAnnualList, [0]), setBillingCycleRow(annualReplace));

  return (monthReplace || annualReplace) ? `<div class="threshold-coupon-terms-wrapper">${tncMessage}</div>` : '';
}

// parse tnc message entry
export const parseTncMessage = () => {
  const getters = store.getters;
  let { tncMessage, couponSourceType } = getters?.couponState || {};

  // don't have terms & condition or verify status is not successful
  if (!tncMessage || getters.verifyCouponStatus !== couponFetchStatus.successfully) {
    return '';
  }

  // for threshold coupon terms and condition
  if (couponSourceType === couponSourceTypeMap.threshold) {
    return parseThresholdTnc();
  }

  const discountPlansMap = getters.discountWithAmountMap?.discountPlans;
  const planList = _.chain(discountPlansMap)
    .keys()
    .map(planCode => {
      return {
        ...discountPlansMap[planCode],
        ...getters.getSelectedPlansMap[planCode],
      };
    })
    .value()
  const discountPlanList = sortByPlanList(planList);

  const selectedPlan = _.get(discountPlanList, [0]);

  if (selectedPlan) {
    const billingCycle = selectedPlan?.billing_cycle;
    const { nextMonthly, nextAnnual } = getNextPstCycleDate({ formatDateTime: dateTime });
    let nextInvoiceDate = billingCycle === BILLING_CYCLE.ANNUAL ? nextAnnual : nextMonthly;
    const couponEndDate = getters.tncCouponEarlyExpireDate;
    const { monthlyCharge, annualCharge } = getters.nextBillAmount;

    const amountWithoutDiscount = Math.max(getters.getSelectedPlansAmount.totalAmount+ getters.openInvoiceTotalAmount, 0);
    const amountWithDiscount = getters.beforeTaxTotalAmount;

    tncMessage = tncMessage.replace(/\${next_invoice_date}/g, nextInvoiceDate);
    tncMessage = tncMessage.replace(/\${subscription_start_date}/g, currentPstDate);
    tncMessage = tncMessage.replace(/\${coupon_end_date}/g, couponEndDate);
    tncMessage = tncMessage.replace(/\${product_name}/g, translation(getOnlinePlanName(selectedPlan)));
    tncMessage = tncMessage.replace(/\${amount_without_discount}/g, transferMoney(amountWithoutDiscount));
    tncMessage = tncMessage.replace(/\${amount_with_discount}/g, transferMoney(amountWithDiscount));
    tncMessage = tncMessage.replace(/\${next_annual_billing_amount}/g, transferMoney(annualCharge));
    tncMessage = tncMessage.replace(/\${next_month_billing_amount}/g, transferMoney(monthlyCharge));

    // new template for edit/add addon/cancellation promo
    tncMessage = parseTncCommonMessage(tncMessage);
  }

  return tncMessage;
}
